'use client'

import { useEffect, type RefObject } from 'react'
import type { AnimatedIconHandle } from './AnimatedCakeIcon'

/** Time between replays. Long enough that a row of icons reads as alive
 *  rather than restless. */
export const ICON_LOOP_PERIOD = 3600
/** Offset between neighbouring icons, so they never twitch in unison. */
export const ICON_LOOP_STAGGER = 450

interface LoopOptions {
  /** false keeps the icon still — e.g. the menu holding it is closed. */
  enabled?: boolean
  period?:  number
  /** This icon's offset within its row, in ms. */
  delay?:   number
}

/**
 * Replays a one-shot animated icon on a slow interval for as long as it is
 * genuinely on screen. The icons ship without a `repeat` option and only
 * expose start/stop, so "always animating" means re-firing startAnimation.
 *
 * The timer runs only while every one of these holds, so an icon nobody can
 * see costs nothing at all:
 *   - `enabled` (its menu is open)
 *   - the host element intersects the viewport — which also covers the
 *     `display:none` desktop nav on a phone, and vice versa, since a
 *     non-rendered element never intersects
 *   - the tab is in the foreground
 *   - the visitor has not asked for reduced motion
 */
export function useAnimatedIconLoop(
  hostRef: RefObject<HTMLElement | null>,
  iconRef: RefObject<AnimatedIconHandle | null>,
  { enabled = true, period = ICON_LOOP_PERIOD, delay = 0 }: LoopOptions = {},
) {
  useEffect(() => {
    const host = hostRef.current
    if (!enabled || !host) return

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
    let kickoff: ReturnType<typeof setTimeout>  | undefined
    let timer:   ReturnType<typeof setInterval> | undefined
    let onScreen = false

    const stop = () => {
      clearTimeout(kickoff)
      clearInterval(timer)
      kickoff = undefined
      timer   = undefined
    }

    /* The kickoff delay is re-applied every time the icon comes back into
       view, which keeps the row's stagger intact after a scroll. */
    const start = () => {
      if (kickoff || timer) return
      kickoff = setTimeout(() => {
        kickoff = undefined
        iconRef.current?.startAnimation()
        timer = setInterval(() => iconRef.current?.startAnimation(), period)
      }, delay)
    }

    const sync = () => {
      if (onScreen && !document.hidden && !reduced.matches) start()
      else stop()
    }

    const io = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting
      sync()
    })
    io.observe(host)
    document.addEventListener('visibilitychange', sync)
    reduced.addEventListener('change', sync)

    return () => {
      stop()
      io.disconnect()
      document.removeEventListener('visibilitychange', sync)
      reduced.removeEventListener('change', sync)
    }
  }, [hostRef, iconRef, enabled, period, delay])
}
